<template>
  <div class="login-wrapper">
    <div class="form-title" v-if="isPhonePwd">支持账号/手机号登录</div>
    <van-form
      class="auth-form"
      ref="formRef"
      required="auto"
      label-align="top"
      validate-trigger="onSubmit"
      @submit="onSubmit"
    >
      <template v-for="col in columns" :key="col.dataIndex">
        <form-input-password
          v-model="formValues[col.dataIndex]"
          :label="col.label"
          :name="col.dataIndex"
          :placeholder="col.placeholder"
          :left-icon="col.icon"
          :message="col.ruleMessage"
          :rules="col.rules"
          :formatter="removeSpaces"
          v-if="col.valueType === 'password'"
        />
        <form-input-verify-code
          v-model="formValues[col.dataIndex]"
          :label="col.label"
          :name="col.dataIndex"
          :placeholder="col.placeholder"
          :left-icon="col.icon"
          :message="col.ruleMessage"
          :required="col.required"
          :login-type="loginType"
          :account="formValues[verifyAccount]"
          :country="checkedCountry?.key"
          :type="1"
          v-else-if="col.valueType === 'verifyCode'"
        />
        <form-input-phone-account
          v-model="formValues[col.dataIndex]"
          :label="col.label"
          :name="col.dataIndex"
          :placeholder="col.placeholder"
          :message="col.ruleMessage"
          :required="col.required"
          :rules="col.rules"
          :isPhone="isPhonePwdPhone"
          @checked="selectCountry"
          v-else-if="col.valueType === 'phoneAccount'"
        />
        <form-input-phone
          v-model="formValues[col.dataIndex]"
          :label="col.label"
          :name="col.dataIndex"
          :placeholder="col.placeholder"
          :left-icon="col.icon"
          :message="col.ruleMessage"
          :required="col.required"
          :rules="col.rules"
          @checked="selectCountry"
          v-else-if="col.valueType === 'phone'"
        />
        <authentication-method-switch v-model="switchType" type="login" v-else-if="col.valueType === 'custom'" />

        <van-field
          class="from-input"
          v-model="formValues[col.dataIndex]"
          :type="col.valueType"
          :name="col.dataIndex"
          :placeholder="col.placeholder"
          :rules="
            col.rules
              ? [{ required: col.required, message: col.ruleMessage }, ...col.rules]
              : [{ required: col.required, message: col.ruleMessage }]
          "
          :formatter="removeSpaces"
          v-bind="col.fieldProps"
          v-else
        >
          <template #right-icon v-if="!!col.icon">
            <svg-icon :icon-name="col.icon" :width="col.icon === 'account' ? 46 : 43" current-color />
          </template>
        </van-field>
      </template>
      <van-cell class="tools" v-if="isAccount || isPhonePwd">
        <van-checkbox v-model="isSaveAccount" shape="round">
          {{ isPhonePwdPhone ? '记住手机号' : '记住账号密码' }}
        </van-checkbox>
        <span class="link-text" @click="showRecoverPasswordPopup">忘记密码？</span>
      </van-cell>

      <div id="aiValidate2" class="ai-validate"></div>

      <van-cell>
        <van-button class="submit-btn" type="primary" native-type="submit" :loading="loading" round block>
          登录
        </van-button>
      </van-cell>
    </van-form>

    <special-verify
      v-model="isShowSpecialDialog"
      :code="specialCode"
      @confirm="specialVerifyConfirm"
      v-if="includes(['1', '2', '3'], specialCode)"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, defineEmits, onMounted, onUnmounted, ref, watch } from 'vue';
import type { FieldProps, FieldRule, FormInstance } from 'vant';
import { showFailToast, showNotify } from 'vant';
import FormInputPassword from '@/components/FormInputPassword/index.vue';
import FormInputVerifyCode from '@/components/FormInputVerifyCode/index.vue';
import FormInputPhone from '@/components/FormInputPhone/index.vue';
import FormInputPhoneAccount from '@/components/FormInputPhoneAccount/index.vue';
import { includes, omitBy, pickBy } from 'lodash-es';
import { storeToRefs, useModalStore, useTenantStore, useUserStore } from '@star/stores';
import { mb_t } from '@star/languages';
import SpecialVerify from './SpecialVerify.vue';
import { AuthStorageKeysEnum, ModalTypeEnum } from '@star/constants';
import { promiseTimeout, StorageSerializers, useLocalStorage } from '@vueuse/core';
import { useAiValidate, useNEGuardian } from '@star/hooks';
import { checkBlankReg, checkEmailReg, checkPhoneReg, isPhoneNumber } from '@star/utils';
import AuthenticationMethodSwitch from '@/layout/header/components/loginRegister/AuthenticationMethodSwitch.vue';

type ColumnData = {
  label?: string;
  valueType?: string;
  dataIndex: string;
  placeholder?: string;
  ruleMessage?: string;
  required?: boolean;
  disabled?: boolean;
  icon?: string;
  show: boolean;
  hidden: boolean;
  fieldProps?: FieldProps;
  rules?: FieldRule[];
};

type Props = {
  visible?: boolean;
  loginType: string;
};

const props = withDefaults(defineProps<Props>(), {
  visible: false,
});

const emit = defineEmits(['success']);

const { login, reqSMSlogin, reqEmailPwdlogin, reqEmaillogin, reqSpecialStatus, fetchVerifyCode } = useUserStore();
const { sysLoginOptions, tenantInfo } = storeToRefs(useTenantStore());
const { getNegProductID, initNeg, getNegToken, token: negToken } = useNEGuardian(); // 网易风控hooks
const modalStore = useModalStore();

const formRef = ref<FormInstance>();
const isSaveAccount = ref<boolean>(true);
const loading = ref<boolean>(false);
const isShowSpecialDialog = ref<boolean>(false);
const checkedCountry = ref<Record<string, any>>();
const specialCode = ref<string>(); // 特殊验证类型 0|1|2|3
const switchType = ref<'1' | '2'>('1'); // 密码或者验证码登录

const formValues = ref<Record<string, any>>({
  cust_name: undefined,
  password: undefined,
  verifycode: undefined, // 图片验证
  validate: undefined, // 三方智能验证code
  special_problems: undefined, // 特殊验证
  app_key: '',

  emailName: undefined,
  emailCode: undefined,

  receiveMobile: undefined,
  smsCode: undefined,

  phone: undefined,
});

const loginApi = {
  account: login,
  phone_captcha: reqSMSlogin,
  email_captcha: reqEmaillogin,
  email_pwd_captcha: reqEmailPwdlogin,
};

const loginSaveAccountInfo = useLocalStorage(
  AuthStorageKeysEnum.LOGIN_SAVE_ACCOUNT_INFO,
  {
    isSave: false,
  },
  {
    serializer: StorageSerializers.object,
  }
);

// 去除所有空格
const removeSpaces = value => {
  return value.replace(/\s+/g, '');
};

// 图形码验证
const isImageVerifyCode = computed(() => {
  return sysLoginOptions.value?.CUST_LOGIN_CAPTCHA_MODE?.PICTURE_SWITCH === '1';
});

// 特殊验证
const isSpecialVerify = computed(() => {
  return sysLoginOptions.value?.CUST_LOGIN_SPECIAL_CAPTCHA === '1';
});

// 易盾智能验证
const isAiVerify = computed(() => {
  return (
    sysLoginOptions.value?.CUST_LOGIN_CAPTCHA_MODE?.SMART_SWITCH === '1' &&
    sysLoginOptions.value?.CUST_LOGIN_CAPTCHA_MODE?.Z2_M_SMART_CAPTCHA_MODE !== '0'
  );
});

const isAccount = computed(() => {
  return props.loginType === 'account';
});

const isEmailCode = computed(() => {
  return props.loginType === 'email_captcha';
});

const isEmailPassword = computed(() => {
  return props.loginType === 'email_pwd_captcha';
});

const isPhone = computed(() => {
  return props.loginType === 'phone_captcha';
});

const verifyAccount = computed(() => {
  if (isPhone.value) {
    return 'receiveMobile';
  } else if (isEmailCode.value) {
    return 'emailName';
  } else if (isPhonePwdPhone.value) {
    return 'phone';
  } else {
    return '';
  }
});

const isPhonePwd = computed(() => {
  return props.loginType === 'phone_pwd_captcha';
});

const isPhonePwdPhone = computed(() => {
  return isPhonePwd.value && isPhoneNumber(formValues.value.phone);
});

const loginMethodSwitch = computed(() => {
  console.log(tenantInfo.value.sysOptions?.LOGIN_METHOD_SWITCH, 'LOGIN_METHOD_SWITCH');
  return tenantInfo.value.sysOptions?.LOGIN_METHOD_SWITCH === '1';
});

const isShowLoginMethodSwitch = computed(() => {
  return (
    loginMethodSwitch.value &&
    isPhonePwd.value &&
    (!formValues.value.phone || (!!formValues.value.phone && isPhonePwdPhone.value))
  );
});

// const commonTriggerList = ['onChange', 'onBlur', 'onSubmit'];

const columns = computed(() => {
  return [
    {
      label: '',
      valueType: 'text',
      dataIndex: 'cust_name',
      placeholder: '用户名',
      ruleMessage: '请输入用户名',
      icon: 'account',
      required: true,
      fieldProps: {
        // maxlength: 4,
        clearable: true,
        formatter: (text: string) => removeSpaces(text),
      },
      rules: [
        {
          validator: (val: string) => {
            if (!checkBlankReg(val)) {
              return '请输入正确的用户名';
            }
            return true;
          },
          trigger: formValues.value.cust_name ? ['onBlur'] : ['onSubmit'],
        },
      ],
      show: isAccount.value,
    },
    {
      label: '',
      valueType: 'phoneAccount',
      dataIndex: 'phone',
      placeholder: '账号/手机号登录',
      ruleMessage: '请输入账号或手机号码',
      icon: 'cellphone',
      required: true,
      fieldProps: {
        // maxlength: 20,
        // minlength: 6,
        clearable: true,
        formatter: (text: string) => removeSpaces(text),
      },
      rules: [
        {
          validator: (val: string) => {
            if (isPhonePwdPhone.value) {
              if (!checkPhoneReg(val)) {
                return '请输入正确的手机号';
              }
            } else {
              const pattern = /^[a-zA-Z0-9]{4,16}$/;
              if (!checkBlankReg(val) || !pattern.test(val)) {
                return '账号格式错误，请输入4-16位英文/数字';
              }
            }
            return true;
          },
          trigger: formValues.value.phone ? ['onBlur'] : ['onSubmit'],
        },
      ],
      show: isPhonePwd.value,
    },
    {
      label: '',
      valueType: 'custom',
      dataIndex: '',
      show: isShowLoginMethodSwitch.value,
    },
    {
      label: '',
      valueType: 'text',
      dataIndex: 'emailName',
      placeholder: '邮箱',
      ruleMessage: '请输入邮箱账号',
      icon: 'email',
      required: true,
      fieldProps: {
        // maxlength: 4,
        clearable: true,
        formatter: (text: string) => removeSpaces(text),
      },
      rules: [
        {
          validator: (val: string) => {
            if (!checkEmailReg(val)) {
              return '请输入正确的邮箱账号';
            }
            return true;
          },
          trigger: formValues.value.emailName ? ['onBlur'] : ['onSubmit'],
        },
      ],
      show: isEmailCode.value || isEmailPassword.value,
    },
    {
      label: '',
      valueType: 'phone',
      dataIndex: 'receiveMobile',
      placeholder: '手机号',
      ruleMessage: '请输入手机号码',
      icon: 'cellphone',
      required: true,
      fieldProps: {
        maxlength: 11,
        clearable: true,
        formatter: (text: string) => removeSpaces(text),
      },
      rules: [
        {
          validator: (val: string) => {
            if (!checkPhoneReg(val)) {
              return '请输入正确的手机号';
            }
            return true;
          },
          trigger: formValues.value.receiveMobile ? ['onBlur'] : ['onSubmit'],
        },
      ],
      show: isPhone.value,
    },
    {
      label: '',
      valueType: 'password',
      dataIndex: 'password',
      placeholder: '密码',
      ruleMessage: '请输入密码',
      icon: 'password',
      required: true,
      show:
        isAccount.value ||
        isEmailPassword.value ||
        (isPhonePwd.value &&
          loginMethodSwitch.value &&
          (switchType.value === '1' || (!!formValues.value.phone && !isPhonePwdPhone.value))) ||
        (isPhonePwd.value && !loginMethodSwitch.value && !isPhonePwdPhone.value),
      fieldProps: {
        formatter: (text: string) => removeSpaces(text),
      },
    },
    {
      label: '',
      valueType: 'verifyCode',
      dataIndex: isImageVerifyCode.value ? 'verifycode' : isEmailCode.value ? 'emailCode' : 'smsCode',
      placeholder: '验证码',
      ruleMessage: '请输入验证码',
      icon: 'yzm_code',
      required: true,
      fieldProps: {
        maxlength: 8,
        clearable: true,
        formatter: (text: string) => removeSpaces(text),
      },
      show:
        isImageVerifyCode.value ||
        isEmailCode.value ||
        isPhone.value ||
        (isPhonePwd.value && switchType.value === '2' && (!formValues.value.phone || isPhonePwdPhone.value)) ||
        (isPhonePwdPhone.value && !loginMethodSwitch.value),
    },
  ].filter(item => item.show) as ColumnData[];
});

const onSubmit = async (values: any) => {
  console.log(values, 'onSubmit');
  loading.value = true;
  await promiseTimeout(800);
  if (isAiVerify.value) {
    if (!isLoad.value) {
      showNotify({ type: 'warning', message: mb_t('智能验证加载失败，请刷新重试！') });
    } else {
      await showVerify(values);
    }
  } else if (isSpecialVerify.value) {
    await getSpecialStatus(values);
  } else {
    await handlerLogin(values);
  }
  loading.value = false;
};

const handlerLogin = async (params: Record<string, any>) => {
  try {
    const negProductId = await getNegProductID();
    await initNeg(negProductId);
    await getNegToken();
    loading.value = true;
    const options: any = {
      token: negToken.value, // 网易风控token
      verifycode: undefined, // 图片验证
      validate: undefined, // 三方智能验证code
      special_problems: undefined, // 特殊验证
      ...omitBy(formValues.value, val => !val),
      ...params,
    };

    console.log(formValues.value, 'handlerLogin formValues.value ++++++++++++++++++');
    console.log(options, 'handlerLogin options ++++++++++++++++++');
    console.log(props.loginType, 'handlerLogin loginType ++++++++++++++++++');
    let _loginType = props.loginType;
    if (isPhonePwd.value) {
      if (isPhonePwdPhone.value && (switchType.value === '2' || !isShowLoginMethodSwitch.value)) {
        _loginType = 'phone_captcha';
        options.receiveMobile = formValues.value?.phone;
        delete options.password;
      } else {
        _loginType = 'account';
        options.cust_name = formValues.value?.phone;
        delete options.smsCode;
      }
      delete options.phone;
    }
    const res = await loginApi[_loginType]?.(options);
    if (res) {
      showNotify({ type: 'success', message: '登录成功' });
      saveAccountHandle(params);
      emit('success');
    }
  } catch (error: any) {
    console.log(error, 'error');
    if (error?.info) {
      showFailToast(error?.info);
    }
  } finally {
    loading.value = false;
  }
};

const saveAccountHandle = (params: Record<string, any>) => {
  if (isSaveAccount.value) {
    loginSaveAccountInfo.value = {
      ...loginSaveAccountInfo.value,
      ...pickBy(formValues.value, (val, key) =>
        includes(['cust_name', 'emailName', 'receiveMobile', 'phone', 'password'], key)
      ),
      isSave: true,
    };
  } else {
    loginSaveAccountInfo.value = {
      isSave: false,
    };
  }
};

const setAccountHandle = () => {
  console.log(props.loginType, loginSaveAccountInfo.value);
  isSaveAccount.value = loginSaveAccountInfo.value.isSave;
  if (loginSaveAccountInfo.value.isSave) {
    switch (props.loginType) {
      case 'account':
        formValues.value.cust_name = loginSaveAccountInfo.value.cust_name;
        formValues.value.password = loginSaveAccountInfo.value.password;
        break;
      case 'email_captcha':
        formValues.value.emailName = loginSaveAccountInfo.value.emailName;
        break;
      case 'email_pwd_captcha':
        formValues.value.emailName = loginSaveAccountInfo.value.emailName;
        formValues.value.password = loginSaveAccountInfo.value.password;
        break;
      case 'phone_pwd_captcha':
        formValues.value.phone = loginSaveAccountInfo.value.phone;
        if (!isPhonePwdPhone.value) {
          formValues.value.password = loginSaveAccountInfo.value.password;
        }
        break;
      case 'phone_captcha':
        formValues.value.receiveMobile = loginSaveAccountInfo.value.receiveMobile;
        break;
    }
  }
};

// 检查特殊验证
const getSpecialStatus = async (params: Record<string, any>) => {
  try {
    loading.value = true;
    let res = await reqSpecialStatus({ cust_name: params.cust_name });
    console.log(res, 'getSpecialStatus');
    if (res) {
      // picCodeCheck.value = res.PICTURE_CAPTCHA_MODE === '1';
      specialCode.value = res.SPECIAL;
      if (includes(['1', '2', '3'], specialCode.value)) {
        isShowSpecialDialog.value = true;
      } else {
        await handlerLogin(params);
      }
    }
  } catch (err: any) {
    showFailToast(err.info);
  } finally {
    loading.value = false;
  }
};

const getFetchVerifyCode = async () => {
  await fetchVerifyCode({ type: 1 });
};

const selectCountry = (data: any) => {
  console.log(data, '000000000000000000000');
  checkedCountry.value = data;
};

const specialVerifyConfirm = async (val: string) => {
  formValues.value.special_problems = val;
  await handlerLogin(formValues.value);
};

const showRecoverPasswordPopup = () => {
  modalStore.showModal(ModalTypeEnum.RECOVER_PASSWORD);
  modalStore.hideModal(ModalTypeEnum.LOGIN_REGISTER_MODAL, !0);
};

const { initCaptcha, unloadCaptcha, showVerify, isLoad } = useAiValidate(1, handlerLogin);

const init = async () => {
  formRef.value?.resetValidation();
  setAccountHandle();
  if (isAiVerify.value) {
    initCaptcha();
  }
  // await getFetchVerifyCode();
};

watch(
  () => isPhonePwdPhone.value,
  value => {
    if (value) {
    }
  }
);

onMounted(async () => {
  await init();
});

onUnmounted(async () => {
  await unloadCaptcha();
});
</script>

<style lang="less" scoped>
.link-text {
  color: var(--text-primary-color);
}

.login-wrapper {
  padding: 0;

  .form-title {
    padding-left: 60px;
    color: #999;
  }

  .ai-validate {
    margin: 30px 50px 0;

    :deep(.yidun_intellisense) {
      .yidun_intelli-control {
        border-radius: 120px;
      }

      .yidun_modal {
        top: -400%;
      }
    }
  }

  .custom-switch-field {
    padding: 0 var(--van-cell-horizontal-padding);

    :deep(.van-field__body) {
      height: auto;
    }

    &::after {
      display: none;
    }

    .switch-item {
      &.active {
        color: var(--theme-primary-color);

        .svg-icon {
          color: var(--theme-primary-color);
        }
      }
    }
  }
}

.theme-card-wrapper {
  padding: 48px 33px;
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 40px;
  background-color: var(--theme-backgroud-color);

  .card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;

    .header-title-wrapper {
      display: flex;
      align-items: center;

      .svg-icon {
        color: var(--theme-primary-color);
        margin-right: 25px;
      }

      .marker {
        display: inline-block;
        width: 14px;
        height: 51px;
        border-radius: 21px;
        background-color: var(--theme-btn-primary-background);
        margin-right: 25px;
      }

      .title {
        font-size: 51px;
        font-weight: 600;
        color: #000;
      }
    }
  }
}
</style>
